import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

/**
 * useAvisoVisto(clave) — persistencia por persona de "avisos de UI ya vistos"
 * (tabla transversal ui_avisos_vistos). Consumidor de los endpoints de Fase 1.
 *
 *   const { visto, cargando, marcarVisto } = useAvisoVisto('onboarding_citas_video');
 *
 * `visto` y `cargando` empiezan en true a propósito: nadie ve el banner hasta que
 * sabemos que NO lo descartó. Fail-closed en lectura (si el GET falla, visto=true).
 */

// Base de la API: MISMO patrón que todo el front. No hay módulo de config
// transversal que la exponga; los ~60 clientes (intervenciones/api.js:8,
// peticiones_API/*, inventario|soporte/api.js, personaContext...) leen esta env
// var inline. En dev es http://localhost:4001; en Azure el front y el backend son
// servicios distintos, así que DEBE ir prefijada (URL relativa iría al :3000).
const API_URL = process.env.REACT_APP_API_URL || '';

// Cabecera Authorization inline a propósito. No existe helper de auth
// transversal: funciones/authHelper.js solo gestiona el 401/sesión caducada, NO
// construye cabeceras; los únicos authHeaders() viven DENTRO de carpetas de
// feature (intervenciones/, inventario/, soporte/...) y un hook transversal no
// debe importar de ahí. Se replica el patrón universal (Bearer de
// localStorage('token'), igual que peticiones_API/* y personaContext).
function authHeaders() {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export function useAvisoVisto(clave) {
  const [visto, setVisto] = useState(true);       // oculto hasta saber
  const [cargando, setCargando] = useState(true);
  const marcadoRef = useRef(false);               // idempotencia de cara al consumidor

  useEffect(() => {
    let cancelado = false;
    const urlMios = `${API_URL}/api/ui-avisos/mios`;
    (async () => {
      try {
        const res = await axios.get(urlMios, {
          params: { claves: clave },
          headers: authHeaders(),
        });
        if (!cancelado) setVisto(Boolean(res?.data?.vistos?.[clave]));
      } catch (e) {
        // Fail-closed: si falla la lectura, se OCULTA el aviso (visto=true). Un
        // aviso accesorio que reaparece a quien ya dijo "no" molesta más que uno
        // que no aparece.
        // Solo en desarrollo: gritar la URL a la que se intentó llamar. El
        // fail-closed silencioso es correcto en prod pero indepurable en local
        // (una base mal resuelta se traga sin rastro y el banner nunca aparece).
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`[useAvisoVisto] GET falló contra "${urlMios}" → aviso oculto (fail-closed). ¿Base REACT_APP_API_URL correcta y backend levantado?`, e?.message || e);
        }
        if (!cancelado) setVisto(true);
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    // Si el componente se desmonta antes de resolver el GET, no hacemos setState.
    return () => { cancelado = true; };
  }, [clave]);

  const marcarVisto = useCallback(() => {
    if (marcadoRef.current) return;   // llamarlo dos veces NO dispara dos POST
    marcadoRef.current = true;
    setVisto(true);                    // optimista, inmediato
    axios
      .post(`${API_URL}/api/ui-avisos/${encodeURIComponent(clave)}/visto`, {}, { headers: authHeaders() })
      .catch((e) => {
        // NO revertimos el estado local: se queda oculto esta sesión y reaparecerá
        // al recargar. Sin toast — el usuario no pidió esta acción como tarea.
        console.error('[useAvisoVisto] POST visto falló:', e?.message || e);
      });
  }, [clave]);

  return { visto, cargando, marcarVisto };
}

export default useAvisoVisto;
